import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Cookie, Settings, X, Shield, ChevronDown, ChevronUp } from 'lucide-react';
import { getToolkitLeadCaptured } from '@/components/toolkit/ToolkitLeadCaptureDialog';

interface ConsentPreferences {
  essential: boolean;
  analytics: boolean;
  advertising: boolean;
  timestamp: string;
}

const CONSENT_KEY = 'cookie_consent_preferences';

export const getConsentPreferences = (): ConsentPreferences | null => {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore parsing errors
  }
  return null;
};

export const hasConsent = (type: 'analytics' | 'advertising'): boolean => {
  const prefs = getConsentPreferences();
  if (!prefs) return false;
  return prefs[type] === true;
};

const TOOLKIT_TOOL_PATH = /^\/toolkit\/[^/]+$/;

const CookieConsent = () => {
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<ConsentPreferences>({
    essential: true,
    analytics: false,
    advertising: false,
    timestamp: '',
  });

  const leadCapturedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const storedConsent = getConsentPreferences();
    if (storedConsent) return;

    const handler = () => {
      leadCapturedTimerRef.current = setTimeout(() => setIsVisible(true), 500);
    };
    window.addEventListener('toolkit-lead-captured', handler);

    const isToolkitToolPage = TOOLKIT_TOOL_PATH.test(location.pathname);
    const leadCaptured = getToolkitLeadCaptured();

    let timer: ReturnType<typeof setTimeout> | null = null;
    if (!isToolkitToolPage || leadCaptured) {
      timer = setTimeout(() => setIsVisible(true), 1000);
    } else {
      // On toolkit tool page with lead form open: show cookie dialog after longer delay so lead shows first
      timer = setTimeout(() => setIsVisible(true), 5000);
    }

    return () => {
      window.removeEventListener('toolkit-lead-captured', handler);
      if (timer) clearTimeout(timer);
      if (leadCapturedTimerRef.current) {
        clearTimeout(leadCapturedTimerRef.current);
        leadCapturedTimerRef.current = null;
      }
    };
  }, [location.pathname]);

  const savePreferences = (prefs: ConsentPreferences) => {
    const withTimestamp = { ...prefs, timestamp: new Date().toISOString() };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(withTimestamp));
    setIsVisible(false);
    
    // Reload to apply consent preferences
    window.location.reload();
  };

  const handleAcceptAll = () => {
    savePreferences({
      essential: true,
      analytics: true,
      advertising: true,
      timestamp: '',
    });
  };

  const handleRejectAll = () => {
    savePreferences({
      essential: true,
      analytics: false,
      advertising: false,
      timestamp: '',
    });
  };

  const handleSavePreferences = () => {
    savePreferences(preferences);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg bg-card border border-border rounded-xl shadow-2xl animate-in slide-in-from-bottom-4 duration-300">
        {/* Header */}
        <div className="p-6 pb-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Cookie className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="font-serif text-lg font-semibold text-foreground">
                  Cookie Preferences
                </h2>
                <p className="text-sm text-muted-foreground">
                  We value your privacy
                </p>
              </div>
            </div>
          </div>

          <p className="text-sm text-muted-foreground mt-4 leading-relaxed">
            We use cookies to enhance your browsing experience, serve personalized ads or content, 
            and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.
          </p>

          {/* Privacy Policy Link */}
          <p className="text-sm text-muted-foreground mt-2">
            Read our{' '}
            <Link to="/privacy-policy" className="text-primary hover:underline font-medium">
              Privacy Policy
            </Link>{' '}
            to learn more.
          </p>
        </div>

        {/* Settings Toggle */}
        <div className="px-6">
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
          >
            <Settings className="w-4 h-4" />
            Customize Settings
            {showSettings ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>

        {/* Detailed Settings */}
        {showSettings && (
          <div className="px-6 py-4 space-y-4 border-t border-border mt-4">
            {/* Essential */}
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-foreground text-sm">Essential Cookies</span>
                  <span className="text-xs bg-muted px-2 py-0.5 rounded">Required</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Necessary for the website to function properly. Cannot be disabled.
                </p>
              </div>
              <Switch checked={true} disabled className="opacity-50" />
            </div>

            {/* Analytics */}
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <span className="font-medium text-foreground text-sm">Analytics Cookies</span>
                <p className="text-xs text-muted-foreground mt-1">
                  Help us understand how visitors interact with our website (Google Analytics).
                </p>
              </div>
              <Switch
                checked={preferences.analytics}
                onCheckedChange={(checked) => 
                  setPreferences(prev => ({ ...prev, analytics: checked }))
                }
              />
            </div>

            {/* Advertising */}
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <span className="font-medium text-foreground text-sm">Advertising Cookies</span>
                <p className="text-xs text-muted-foreground mt-1">
                  Used to deliver personalized advertisements (Google AdSense).
                </p>
              </div>
              <Switch
                checked={preferences.advertising}
                onCheckedChange={(checked) => 
                  setPreferences(prev => ({ ...prev, advertising: checked }))
                }
              />
            </div>

            {/* CCPA Notice */}
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 mt-4">
              <div className="flex items-start gap-2">
                <Shield className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs font-medium text-amber-700 dark:text-amber-300">
                    California Residents (CCPA)
                  </p>
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                    By disabling advertising cookies, you are exercising your right to opt-out of the 
                    "sale" of your personal information.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="p-6 pt-4 border-t border-border flex flex-col sm:flex-row gap-3">
          {showSettings ? (
            <>
              <Button 
                variant="outline" 
                className="flex-1" 
                onClick={handleRejectAll}
              >
                Reject All
              </Button>
              <Button 
                className="flex-1" 
                onClick={handleSavePreferences}
              >
                Save Preferences
              </Button>
            </>
          ) : (
            <>
              <Button 
                variant="outline" 
                className="flex-1" 
                onClick={handleRejectAll}
              >
                Reject All
              </Button>
              <Button 
                className="flex-1" 
                onClick={handleAcceptAll}
              >
                Accept All
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
